import JSZip from "jszip"
import { jsonToPoint } from '~/helpers/database-conversions'
import { IndexedTranslation } from '~/server/export/translation/chapter-translations'
import { getChapter } from '~/services/database/chapters'
import { getSeries } from '~/services/database/series'

const getODS = async (chapterId: string, translations: IndexedTranslation[]) => {
  const chapter = await getChapter(chapterId)
  const series = await getSeries(chapter.series_id)

  const zip = new JSZip()

  // Add the mimetype file (uncompressed and first in the archive)
  zip.file("mimetype", "application/vnd.oasis.opendocument.spreadsheet", { compression: "STORE" })

  // Add META-INF/manifest.xml
  zip.folder("META-INF")?.file(
    "manifest.xml",
    `<?xml version="1.0" encoding="UTF-8"?>
    <manifest:manifest xmlns:manifest="urn:oasis:names:tc:opendocument:xmlns:manifest:1.0">
        <manifest:file-entry manifest:full-path="/" manifest:media-type="application/vnd.oasis.opendocument.spreadsheet"/>
        <manifest:file-entry manifest:full-path="content.xml" manifest:media-type="text/xml"/>
    </manifest:manifest>`
  )

  // Build content with table structure
  let content = ''
  
  // Create table for series info
  content += '<table:table table:name="Translations">'
  
  // Series info
  content += `<table:table-row><table:table-cell office:value-type="string"><text:p>Series Title</text:p></table:table-cell></table:table-row>`
  content += `<table:table-row><table:table-cell office:value-type="string"><text:p>${series?.title || ''}</text:p></table:table-cell></table:table-row>`
  content += `<table:table-row><table:table-cell office:value-type="string"><text:p>Series Author</text:p></table:table-cell></table:table-row>`
  content += `<table:table-row><table:table-cell office:value-type="string"><text:p>${series?.author || ''}</text:p></table:table-cell></table:table-row>`
  content += `<table:table-row><table:table-cell office:value-type="string"><text:p>Series Client</text:p></table:table-cell></table:table-row>`
  content += `<table:table-row><table:table-cell office:value-type="string"><text:p>${series?.client || ''}</text:p></table:table-cell></table:table-row>`
  content += `<table:table-row></table:table-row>`

  // Chapter info
  content += `<table:table-row><table:table-cell office:value-type="string"><text:p>Chapter</text:p></table:table-cell></table:table-row>`
  content += `<table:table-row><table:table-cell office:value-type="string"><text:p>${chapter.index}</text:p></table:table-cell></table:table-row>`
  
  if(chapter.title) {
    content += `<table:table-row><table:table-cell office:value-type="string"><text:p>Chapter Title</text:p></table:table-cell></table:table-row>`
    content += `<table:table-row><table:table-cell office:value-type="string"><text:p>${chapter.title}</text:p></table:table-cell></table:table-row>`
  }
  
  content += `<table:table-row></table:table-row>`

  // Headers
  content += `<table:table-row>
    <table:table-cell office:value-type="string"><text:p>Page Index</text:p></table:table-cell>
    <table:table-cell office:value-type="string"><text:p>Entry Index</text:p></table:table-cell>
    <table:table-cell office:value-type="string"><text:p>Position</text:p></table:table-cell>
    <table:table-cell office:value-type="string"><text:p>Translated Text</text:p></table:table-cell>
  </table:table-row>`

  // Translation entries
  let pageIndex = 0
  let currentPageId: number | null = null

  translations.forEach((entry) => {
    if (currentPageId !== entry.page_id) {
      currentPageId = entry.page_id
      pageIndex++
    }

    const point = jsonToPoint(entry.start_point)
    const position = `${Math.round(point.x * 100)},${Math.round(point.y * 100)}`
    
    content += `<table:table-row>
      <table:table-cell office:value-type="float" office:value="${pageIndex}"><text:p>${pageIndex}</text:p></table:table-cell>
      <table:table-cell office:value-type="float" office:value="${entry.index + 1}"><text:p>${entry.index + 1}</text:p></table:table-cell>
      <table:table-cell office:value-type="string"><text:p>${position}</text:p></table:table-cell>
      <table:table-cell office:value-type="string"><text:p>${entry.translated_text ?? ''}</text:p></table:table-cell>
    </table:table-row>`
  })

  content += '</table:table>'

  // Add content.xml with the generated content
  zip.file(
    "content.xml",
    `<?xml version="1.0" encoding="UTF-8"?>
    <office:document-content 
        xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0"
        xmlns:text="urn:oasis:names:tc:opendocument:xmlns:text:1.0"
        xmlns:table="urn:oasis:names:tc:opendocument:xmlns:table:1.0"
        xmlns:number="urn:oasis:names:tc:opendocument:xmlns:datastyle:1.0"
        office:version="1.2"
        office:mimetype="application/vnd.oasis.opendocument.spreadsheet">
        <office:body>
            <office:spreadsheet>
                ${content}
            </office:spreadsheet>
        </office:body>
    </office:document-content>`
  )

  // Generate the ZIP archive
  const blob = await zip.generateAsync({
    type: "blob",
    mimeType: "application/vnd.oasis.opendocument.spreadsheet",
    compression: "DEFLATE", // Compress all files except mimetype
  })

  return blob
}

export { getODS }